import { ChangeEvent, KeyboardEvent, useState } from 'react';

import { InputLabel, Stack, TextField } from '@mui/material';

import ArrowRightIcon from '@/components/Icons/ArrowRightIcon';
import { OrganizationJoinExistingSetting } from '@/graphql';
import useOrg from '@/hooks/useOrg';
import { useUpdateOrganizationMutation } from '@/hooks/useOrg/__generated__/mutations.generated';
import JoinExistingSettings from '@/sections/JoinExistingSettings';
import { useAnalytics } from '@/utils/analytics/segment/hooks';
import { useSnackbar } from 'notistack';

import { useExistingOrganizationQuery } from '../../__generated__/queries.generated';
import { useCompleteOnboarding, useExitOnboarding } from '../../hooks';
import { OnboardingEvent } from '../../types';
import { Body, Container, Description, Header, NextButton, Title } from '../styled';
import { saveWorkspace } from './utils';

function WorkspaceStep() {
  const analytics = useAnalytics();
  const { data: existingOrganizationData, loading: existingOrganizationDataLoading } =
    useExistingOrganizationQuery({
      fetchPolicy: 'no-cache',
    });
  const org = useOrg();
  const [updateOrg] = useUpdateOrganizationMutation();
  const [workspaceName, setWorkspaceName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const completeOnboarding = useCompleteOnboarding();
  const exitOnboarding = useExitOnboarding();

  async function nextStep() {
    await completeOnboarding();

    exitOnboarding();
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    setWorkspaceName(e.currentTarget.value);
  }

  function handleEnter(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  }

  async function handleSubmit() {
    setLoading(true);
    try {
      await saveWorkspace(workspaceName);
      analytics.track(OnboardingEvent.NamedWorkflow);

      await updateOrg({ variables: { workspaceName } });
      nextStep();
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }

  const isLoading = existingOrganizationDataLoading || loading;
  const showJoinExistingSettings =
    !existingOrganizationDataLoading &&
    !existingOrganizationData?.existingOrganization &&
    [OrganizationJoinExistingSetting.Enabled, OrganizationJoinExistingSetting.Disabled].includes(
      org?.joinExistingSetting,
    );

  return (
    <Container>
      <Header>
        <Title data-cy="onb-title">Next, let&#39;s name your workspace.</Title>
        <Description>
          This could be the name of your company, team, project, or something else.
        </Description>
      </Header>
      <Body>
        <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
          <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
            <InputLabel htmlFor="workspace-name">Workspace name</InputLabel>

            <TextField
              fullWidth
              id="workspace-name"
              name="workspaceName"
              variant="outlined"
              inputProps={{
                'data-cy': 'onb-workspace-name',
              }}
              placeholder="e.g. Company name"
              onChange={handleChange}
              onKeyDown={handleEnter}
              value={workspaceName}
              autoFocus
            />
          </Stack>
          {showJoinExistingSettings && (
            <Stack>
              <JoinExistingSettings />
            </Stack>
          )}
        </Stack>
        <Stack alignItems="end" width="100%">
          <NextButton
            onClick={handleSubmit}
            disabled={workspaceName.trim().length === 0}
            data-cy="onb-submit"
            loading={isLoading}
            endIcon={<ArrowRightIcon />}
          >
            Continue
          </NextButton>
        </Stack>
      </Body>
    </Container>
  );
}

export default WorkspaceStep;
